// src/menu-items.tsx
import {
  PartnersIcon,
  LendingIcon,
  CompanyIcon,
  TeamIcon,
  OnboardingIcon,
  DebitIcon
} from "@clara/icons";
import { isEmptyArray, pipe, when } from "@clara/utils";
import { jsx } from "react/jsx-runtime";
var itemsByPrivileges = [
  {
    title: "onboarding",
    icon: /* @__PURE__ */ jsx(OnboardingIcon, {}),
    subNav: [
      {
        title: "onboarding",
        id: "/onboarding"
      }
    ]
  },
  {
    title: "account",
    icon: /* @__PURE__ */ jsx(CompanyIcon, {}),
    subNav: [
      {
        title: "insights",
        id: "/dashboard"
      },
      {
        title: "transactions",
        id: "/transactions"
      }
    ]
  }
];
var managementItems = [
  {
    title: "management",
    icon: /* @__PURE__ */ jsx(TeamIcon, {}),
    subNav: [
      {
        title: "cards",
        id: "/cards"
      },
      {
        title: "team",
        id: "/users"
      },
      {
        title: "invoices",
        id: "/invoices"
      },
      {
        title: "company",
        id: "/company"
      },
      {
        title: "activity",
        id: "/activity-logs"
      }
    ]
  }
];
var growthItems = [
  {
    title: "growth",
    icon: /* @__PURE__ */ jsx(PartnersIcon, {}),
    subNav: [
      {
        title: "partners",
        id: "/benefits/partners"
      },
      {
        title: "points",
        id: "/benefits/points"
      }
    ]
  }
];
var paymentsItems = [
  {
    title: "payments",
    icon: /* @__PURE__ */ jsx(DebitIcon, {}),
    subNav: []
  }
];
var PaymentsItem = {
  title: "thirdPartyPayment",
  id: "/payments"
};
var BillPayItem = {
  title: "billPay",
  id: "/payments"
};
var ServicesItem = {
  title: "services",
  id: "/services"
};
var ReferralsItem = {
  title: "referrals",
  id: "/referrals"
};
var LendingItem = {
  title: "lending",
  id: "/lending"
};
var InternationalPaymentsItem = {
  title: "internationalPayments",
  id: "/international-payments"
};
var DigitalAccountPaymentsItem = {
  title: "digitalAccountPayments",
  id: "/digital-account"
};
var digitalAccountPaymentsItems = [
  {
    title: "digitalAccount",
    icon: /* @__PURE__ */ jsx(LendingIcon, {}),
    subNav: []
  }
];
var digitalAccountItems = [
  {
    title: "digitalAccount",
    icon: /* @__PURE__ */ jsx(LendingIcon, {}),
    subNav: []
  }
];
var DigitalAccountPanelItem = {
  title: "panel",
  id: "/digital-account"
};
var DigitalAccountSettingsItem = {
  title: "settings",
  id: "/settings-digital-account"
};
var DigitalAccountPixItem = {
  title: "pix",
  id: "/digital-account/pix-area"
};
var afterSlash = (s) => {
  const match = s.match(/\/([^/]+)/);
  return match ? match[1] : "";
};
var toUpper = (s) => s.toUpperCase();
var toSubject = pipe(afterSlash, toUpper);
var canPass = (ability) => (item) => {
  if (!item.id)
    return false;
  const subject = toSubject(item.id);
  return ability.can("SEARCH", subject) || ability.can("SERVICES", subject);
};
var filterNavItem = (pred) => ({ subNav, ...item }) => ({
  ...item,
  subNav: subNav == null ? void 0 : subNav.filter(pred)
});
var byAbility = (ability) => (items) => items.map(filterNavItem(canPass(ability))).filter((x) => !isEmptyArray(x.subNav));
var addSubMenuItem = (item) => (items) => {
  var _a;
  const [head] = items;
  const subNav = (_a = head.subNav) == null ? void 0 : _a.concat(item);
  return [{ ...head, subNav }];
};
var removeItemById = (item, itemId) => {
  const [head] = item;
  if (!itemId || !head.subNav)
    return item;
  return [
    {
      ...head,
      subNav: head.subNav.reduce((acc, curr) => {
        if (curr.id === itemId)
          return acc;
        return [...acc, curr];
      }, [])
    }
  ];
};
var addManagementItems = (role, ability, user, country) => (items) => {
  var _a;
  const InvoicesItem = (_a = managementItems[0].subNav) == null ? void 0 : _a.find(
    (x) => x.id === "/invoices"
  );
  if (country === "MX") {
    const { can_access_invoices_company, can_access_invoices_user } = user;
    const can = canPass(ability);
    if (InvoicesItem) {
      const cannotInvoices = () => !can_access_invoices_company || !can_access_invoices_user || !can(InvoicesItem);
      const filterByAbility = (xs) => xs.map(filterNavItem(can));
      const enhanceInvoices = pipe(
        filterByAbility,
        when(
          cannotInvoices,
          (item) => removeItemById(item, InvoicesItem.id)
        )
      )(managementItems);
      return items.concat(enhanceInvoices);
    }
  }
  return items.concat(
    InvoicesItem ? removeItemById(managementItems, InvoicesItem.id) : managementItems
  );
};
var addGrowthItems = (role, ability, user, country) => (items) => {
  const can = canPass(ability);
  const canLending = () => role === "COMPANYOWNER" && can(LendingItem) && (user == null ? void 0 : user.lendingEnabledUser);
  const filterByAbility = (xs) => xs.map(filterNavItem(can));
  const enhanceGrowth = pipe(
    filterByAbility,
    addSubMenuItem(ReferralsItem),
    // Referrals will always be added
    when(canLending, addSubMenuItem(LendingItem))
  )(growthItems);
  return items.concat(enhanceGrowth);
};
var addPaymentsItems = (user, ability, country) => (items) => {
  switch (country) {
    case "BR": {
      const { can_access_bill_pay_user, can_access_bill_pay_company } = user;
      const canPayments = () => can_access_bill_pay_user && can_access_bill_pay_company && canPass(ability)(BillPayItem);
      const enhancePayments = pipe(
        when(canPayments, addSubMenuItem(BillPayItem))
      )(paymentsItems);
      const [head] = enhancePayments;
      const result = when(
        () => !isEmptyArray(head == null ? void 0 : head.subNav),
        (xs) => xs.concat(enhancePayments)
      )(items);
      return result;
    }
    case "MX": {
      const {
        can_make_payments_company,
        can_pay_services_company,
        internationalPaymentsCompanyEnable
      } = user;
      const canPayments = () => can_make_payments_company && canPass(ability)(PaymentsItem);
      const canInternationalPayments = () => !!internationalPaymentsCompanyEnable && canPass(ability)(InternationalPaymentsItem);
      const canServices = () => can_pay_services_company && canPass(ability)(ServicesItem);
      const enhancePayments = pipe(
        when(canPayments, addSubMenuItem(PaymentsItem)),
        when(
          canInternationalPayments,
          addSubMenuItem(InternationalPaymentsItem)
        ),
        when(canServices, addSubMenuItem(ServicesItem))
      )(paymentsItems);
      const [head] = enhancePayments;
      const result = when(
        () => !isEmptyArray(head == null ? void 0 : head.subNav),
        (xs) => xs.concat(enhancePayments)
      )(items);
      return result;
    }
    default:
      return items;
  }
};
var addDigitalAccountItems = (user, ability, country) => (items) => {
  switch (country) {
    case "BR": {
      const {
        can_add_debit_company,
        can_add_debit,
        can_access_pix_user,
        can_access_pix_company
      } = user;
      const canDigitalAccount = () => can_add_debit_company && can_add_debit;
      const canSettingsDigitalAccount = () => can_add_debit_company && can_add_debit && (user == null ? void 0 : user.role) === "COMPANYOWNER";
      const canPixDigitalAccount = () => can_access_pix_user && can_access_pix_company;
      const enhanceDigitalAccount = pipe(
        when(canDigitalAccount, addSubMenuItem(DigitalAccountPanelItem)),
        when(
          canSettingsDigitalAccount,
          addSubMenuItem(DigitalAccountSettingsItem)
        ),
        when(canPixDigitalAccount, addSubMenuItem(DigitalAccountPixItem))
      )(digitalAccountItems);
      const [head] = enhanceDigitalAccount;
      const result = when(
        () => !isEmptyArray(head == null ? void 0 : head.subNav),
        (xs) => xs.concat(enhanceDigitalAccount)
      )(items);
      return result;
    }
    case "MX": {
      const { can_add_debit_company, can_add_debit } = user;
      const canDigitalAccount = () => can_add_debit_company && can_add_debit && canPass(ability)(DigitalAccountPaymentsItem);
      const enhanceDigitalAccount = pipe(
        when(canDigitalAccount, addSubMenuItem(DigitalAccountPaymentsItem))
      )(digitalAccountPaymentsItems);
      const [head] = enhanceDigitalAccount;
      const result = when(
        () => !isEmptyArray(head == null ? void 0 : head.subNav),
        (xs) => xs.concat(enhanceDigitalAccount)
      )(items);
      return result;
    }
    default:
      return items;
  }
};
var getItems = (items) => (ability, user, country) => pipe(
  addManagementItems(user == null ? void 0 : user.role, ability, user, country),
  byAbility(ability),
  addDigitalAccountItems(user, ability, country),
  addPaymentsItems(user, ability, country),
  addGrowthItems(user == null ? void 0 : user.role, ability, user, country)
)(items);
var canRender = getItems(itemsByPrivileges);
var menu_items_default = canRender;

export {
  menu_items_default
};
